import { FC, memo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer, Typography, useMediaQuery } from '@mui/material';
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import MiniDrawerStyled, { closedMixin, openedMixin } from './MiniDrawerStyled';
import { DRAWER_WIDTH } from '../../../config';
import { FormattedMessage } from 'react-intl';
import { useMenu } from '../../../store/menu';

interface Props {
	window?: () => Window;
}

const SidebarInner: FC<{ drawerOpen: boolean }> = memo(({ drawerOpen }) => {
	return (
		<Stack height={'100%'}>
			<DrawerHeader open={drawerOpen} />
			<DrawerContent />
			<Typography variant={'caption'} align={'center'} paddingBottom={2}>
				<FormattedMessage id={'footer.caption'} />
			</Typography>
		</Stack>
	);
});

const MainDrawer: FC = ({ window }: Props) => {
	const theme = useTheme();
	const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));

	const { state: menu, openDrawer } = useMenu();
	const { drawerOpen } = menu;

	// responsive drawer container
	const container = window !== undefined ? () => window().document.body : undefined;

	return (
		<Box
			component={'nav'}
			height={'100%'}
			position={{ xs: 'fixed', lg: 'relative' }}
			gridArea={{ lg: 'sidebar' }}
			sx={{
				backgroundColor: (theme) => theme.palette.background.paper,
				borderRight: `1px solid ${theme.palette.divider}`,
			}}
		>
			{!matchDownMD ? (
				<MiniDrawerStyled
					sx={{
						height: '100%',
						...(drawerOpen
							? {
									...openedMixin(theme),
								}
							: {
									...closedMixin(theme),
								}),
					}}
				>
					<SidebarInner drawerOpen={drawerOpen} />
				</MiniDrawerStyled>
			) : (
				<Drawer
					container={container}
					variant={'temporary'}
					open={drawerOpen}
					onClose={() => openDrawer(!drawerOpen)}
					ModalProps={{ keepMounted: true }}
					sx={{
						display: { xs: 'block', lg: 'none' },
						height: '100%',
						pointerEvents: drawerOpen ? undefined : 'none',
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: DRAWER_WIDTH,
							backgroundImage: 'none',
							boxShadow: 'inherit',
						},
					}}
				>
					<SidebarInner drawerOpen={drawerOpen} />
				</Drawer>
			)}
		</Box>
	);
};

export default MainDrawer;
